import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";

const CartContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CartProvider = ({ children }) => {
  const { token, user } = useAuth();
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (token && user) {
      fetchCart();
    } else {
      setCart([]);
    }
  }, [token, user]);

  const fetchCart = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/cart`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCart(response.data);
    } catch (error) {
      console.error("Cart fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const addToCart = async (config, quantity = 1) => {
    if (!token) throw new Error("Please login to add items to cart");
    
    const response = await axios.post(
      `${API}/cart/add`,
      { config, quantity },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    await fetchCart();
    return response.data;
  };

  const removeFromCart = async (itemId) => {
    if (!token) return;
    await axios.delete(`${API}/cart/${itemId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    await fetchCart();
  };

  const clearCart = async () => {
    if (!token) return;
    await axios.delete(`${API}/cart`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setCart([]);
  };

  const cartTotal = cart.reduce((sum, item) => sum + item.price, 0);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider value={{ cart, loading, addToCart, removeFromCart, clearCart, cartTotal, cartCount, fetchCart }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within CartProvider");
  }
  return context;
};
